import React, { FC, useState } from 'react';
import {
  EdgeProps,
  BaseEdge,
  getBezierPath,
  EdgeLabelRenderer,
  useReactFlow,
} from 'reactflow';
import { X } from 'lucide-react';
import { CANVAS_STATUS_COLORS } from '../../constants';

const DeletableEdge: FC<EdgeProps> = ({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  style = {},
  markerEnd,
  label,
  selected,
  interactionWidth = 20,
}) => {
  const { setEdges } = useReactFlow();
  const [hovered, setHovered] = useState(false);
  const [deleteHovered, setDeleteHovered] = useState(false);
  const [edgePath, labelX, labelY] = getBezierPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  });

  const onDelete = (event: React.MouseEvent) => {
    event.stopPropagation();
    setEdges((edges) => edges.filter((e) => e.id !== id));
  };

  const showButton = hovered || selected;
  const interactiveStyle: React.CSSProperties = {
    ...style,
    stroke: selected ? '#0d9488' : hovered ? CANVAS_STATUS_COLORS.active : style.stroke,
    strokeWidth: selected ? 3 : hovered ? 2.25 : style.strokeWidth,
    transition: 'stroke 120ms ease, stroke-width 120ms ease',
  };

  return (
    <>
      <g
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        <BaseEdge
          id={id}
          path={edgePath}
          style={interactiveStyle}
          markerEnd={markerEnd}
          interactionWidth={interactionWidth}
        />
      </g>
      {label && (
        <EdgeLabelRenderer>
          <div
            style={{
              position: 'absolute',
              transform: `translate(-50%, -130%) translate(${labelX}px,${labelY}px)`,
              pointerEvents: 'all',
            }}
            className="text-xs text-slate-500 dark:text-slate-400 bg-white dark:bg-slate-900 px-1.5 py-0.5 rounded border border-slate-200 dark:border-slate-700"
          >
            {label}
          </div>
        </EdgeLabelRenderer>
      )}
      <EdgeLabelRenderer>
        <button
          className="edge-delete-btn"
          style={{
            position: 'absolute',
            transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
            pointerEvents: 'all',
            opacity: showButton ? 1 : 0,
            width: 18,
            height: 18,
            minWidth: 18,
            minHeight: 18,
            boxSizing: 'border-box',
            borderRadius: '50%',
            border: `1px solid ${deleteHovered ? CANVAS_STATUS_COLORS.error : '#cbd5e1'}`,
            background: '#f1f5f9',
            color: deleteHovered ? CANVAS_STATUS_COLORS.error : '#64748b',
            display: 'grid',
            placeItems: 'center',
            padding: 0,
            margin: 0,
          }}
          onClick={onDelete}
          onMouseEnter={() => {
            setHovered(true);
            setDeleteHovered(true);
          }}
          onMouseLeave={() => {
            setHovered(false);
            setDeleteHovered(false);
          }}
        >
          <X
            size={10}
            strokeWidth={2.5}
            style={{
              position: 'absolute',
              left: '50%',
              top: '50%',
              transform: 'translate(-50%, -50%)',
              display: 'block',
              pointerEvents: 'none',
            }}
            aria-hidden="true"
          />
        </button>
      </EdgeLabelRenderer>
    </>
  );
};

export default DeletableEdge;
